<template>
  <Layout title="Customers">
    <div class="report-content">
      <div class="report-heading">
        <div>
          <h1>Customers</h1>
          <p>Explore shipping performance for each customer.</p>
        </div>
      </div>
      <div class="report-filters">
        <CustomersPicker
          id="customers-list"
          :customers="customers"
          @update:customer="handleCustomerChanged"
        /><DateRangePicker id="date-range" :value="dateRangeValue" @update:value="handleDateChanged" />
      </div>
      <b-row>
        <b-col>
          <HitMap :markers="mapMarkers" :stats="stats" :loading="statsLoading" :error="statsError" />
        </b-col>
      </b-row>

      <b-row>
        <b-col lg="6">
          <CarriersTable
            :table-data="deliveryTime"
            title="Average Delivery Time"
            col-title="Days"
            block-id="DeliveryTime"
            @update:distribution-type-delivery-time="changeDistributionTypeDeliveryTime"
          />
        </b-col>
        <b-col lg="6">
          <CarriersTable
            :table-data="usage"
            title="Carrier Usage"
            col-title="Usage"
            block-id="Usage"
            @update:distribution-type-usage="changeDistributionTypeUsage"
          />
        </b-col>
      </b-row>

      <b-row>
        <b-col lg="6">
          <AveragePackagePrices
            :table-data="averagePrices"
            title="Average Package Prices"
            :filters="filters"
            @update:service-level="changeServiceLevel"
          />
        </b-col>
        <b-col lg="6">
          <PeriodChart :period-data="totalSales" title="Shipping Turnover" unit="kr" />
        </b-col>
      </b-row>

      <b-row>
        <b-col lg="6">
          <PeriodChart :period-data="shipmentsCreated" title="Shipments Created" />
        </b-col>
        <b-col lg="6">
          <PeriodChart :period-data="shipmentsSent" title="Shipments Sent" />
        </b-col>
      </b-row>
    </div>
  </Layout>
</template>
<script lang="ts">
import type { Filter } from '@/typings/Filter'
import type { Periods } from '@/typings/Periods'
import type { PeriodsDiff } from '@/typings/Shipments/PeriodsDiff'
import type { MarkerItem } from '@/typings/Shipments/MarkerItem'
import type { DataCollection } from '@/typings/DataCollection'
import type { CustomerData } from '@/typings/Customers/CustomerData'
import type { ComponentData } from '@/typings/Customers/ComponentData'
import type { ShipmentStatisticItem, StatisticsBlockItem } from '@/typings/Shipments/ShipmentStatisticItem'
import { defineComponent, ref, onMounted, watch, type Ref } from 'vue'
import HitMap from '@/views/dashboard/partials/map.vue'
import Layout from '@/views/dashboard/analytics-shell.vue'
import PeriodChart from '@/views/dashboard/partials/period-chart.vue'
import ErrorHandler from '@/requests/ErrorHandler'
import { withDeliveryAvailability } from '@/utils/delivery-availability'
import CarriersTable from '@/views/dashboard/partials/carrier-table-with-diff.vue'
import CustomersPicker from '@/views/dashboard/partials/report-customers.vue'
import DateRangePicker from '@/views/dashboard/partials/report-period.vue'
import AveragePackagePrices from '@/views/dashboard/partials/average-package-prices.vue'
import Shipments from '@/requests/Shipments'
import moment from 'moment/moment'

export default defineComponent<ComponentData>({
  components: { PeriodChart, HitMap, CarriersTable, DateRangePicker, CustomersPicker, Layout, AveragePackagePrices },
  setup() {
    const shipmentsRequest = Shipments()

    const createFilters = (): Filter => ({
      period: dateRangeValue.value,
      customerId: customerValue.value,
      distributionChannel: null,
      serviceLevel: null,
    })

    const dateRangeValue: Ref<string> = ref(
      moment().subtract(29, 'days').format('YYYY-MM-DD') + ' - ' + moment().format('YYYY-MM-DD'),
    )
    const customerValue: Ref<string | null> = ref(null)

    const mapMarkers: Ref<MarkerItem[]> = ref([])
    const totalSales: Ref<Periods> = ref({ currentPeriod: [], previousPeriod: [], periodLabels: [] })
    const shipmentsSent: Ref<Periods> = ref({ currentPeriod: [], previousPeriod: [], periodLabels: [] })
    const shipmentsCreated: Ref<Periods> = ref({ currentPeriod: [], previousPeriod: [], periodLabels: [] })
    const averagePrices: Ref<PeriodsDiff[]> = ref([])
    const deliveryTime: Ref<PeriodsDiff[]> = ref([])
    const usage: Ref<PeriodsDiff[]> = ref([])
    const customers: Ref<CustomerData[]> = ref([])
    const filters: Ref<Filter> = ref(createFilters())
    const statsLoading = ref(true)
    const statsError = ref(false)
    let statsRequest = 0
    const stats: Ref<ShipmentStatisticItem[]> = ref([
      { icon: 'uil uil-window-restore float-end', number: '', title: 'Info Received', subtext: '' },
      { icon: 'mdi mdi-archive-check-outline float-end', number: '', title: 'Delivered', subtext: '' },
      { icon: 'mdi mdi-alarm float-end', number: '', title: 'In Transit', subtext: '' },
      { icon: 'uil uil-window-restore float-end', number: '', title: 'Out for Delivery', subtext: '' },
      { icon: 'mdi mdi-archive-check-outline float-end', number: '', title: 'Failed Attempt', subtext: '' },
      { icon: 'mdi mdi-alarm float-end', number: '', title: 'Exception', subtext: '' },
    ])

    const distributionTypeDeliveryTime: Ref<string> = ref('package')
    const distributionTypeUsage: Ref<string> = ref('package')
    const serviceLevel: Ref<string> = ref('priority')

    const changeDistributionTypeDeliveryTime = (value: string) => {
      distributionTypeDeliveryTime.value = value
      getDeliveryTime({ ...createFilters(), distributionChannel: value })
    }

    const changeDistributionTypeUsage = (value: string) => {
      distributionTypeUsage.value = value
      getUsage({ ...createFilters(), distributionChannel: value })
    }

    const changeServiceLevel = (value: string) => {
      serviceLevel.value = value
      getAveragePackagePrices({ ...createFilters(), serviceLevel: value })
    }

    const handleDateChanged = (value: string): void => {
      dateRangeValue.value = value
      filters.value.period = value
    }

    const handleCustomerChanged = (value: string): void => {
      customerValue.value = value
      filters.value.customerId = value
    }

    const getStatsData = (filters: Filter): void => {
      const request = ++statsRequest
      statsLoading.value = true
      statsError.value = false
      shipmentsRequest
        .getStatsData(filters)
        .then((data: DataCollection<PeriodsDiff>) => {
          if (request !== statsRequest) return
          const defaultData: PeriodsDiff = { currentPeriod: '0', previousPeriod: '0', diff: '0', name: '' }
          let defStat: StatisticsBlockItem = {
            infoReceived: defaultData,
            delivered: defaultData,
            inTransit: defaultData,
            outForDelivery: defaultData,
            failedAttempt: defaultData,
            exception: defaultData,
          }
          Object.keys(defStat).forEach((key: string) => {
            defStat[key as keyof StatisticsBlockItem] =
              data.data.find((item: PeriodsDiff) => item.name === key) || defaultData
          })
          stats.value = [
            {
              icon: 'uil uil-window-restore float-end',
              number: defStat.infoReceived.currentPeriod,
              title: 'Info Received',
              subtext: defStat.infoReceived.diff,
            },
            {
              icon: 'mdi mdi-archive-check-outline float-end',
              number: defStat.delivered.currentPeriod,
              title: 'Delivered',
              subtext: defStat.delivered.diff,
            },
            {
              icon: 'mdi mdi-alarm float-end',
              number: defStat.inTransit.currentPeriod,
              title: 'In Transit',
              subtext: defStat.inTransit.diff,
            },
            {
              icon: 'uil uil-window-restore float-end',
              number: defStat.outForDelivery.currentPeriod,
              title: 'Out for Delivery',
              subtext: defStat.outForDelivery.diff,
            },
            {
              icon: 'mdi mdi-archive-check-outline float-end',
              number: defStat.failedAttempt.currentPeriod,
              title: 'Failed Attempt',
              subtext: defStat.failedAttempt.diff,
            },
            {
              icon: 'mdi mdi-alarm float-end',
              number: defStat.exception.currentPeriod,
              title: 'Exception',
              subtext: defStat.exception.diff,
            },
          ]
        })
        .catch((error) => {
          if (request !== statsRequest) return
          statsError.value = true
          ErrorHandler.handle(error)
        })
        .finally(() => {
          if (request === statsRequest) statsLoading.value = false
        })
    }

    const getCitiesData = (filters: Filter): void => {
      shipmentsRequest
        .getCitiesData(filters)
        .then((data: DataCollection<MarkerItem>) => {
          mapMarkers.value = data.data
        })
        .catch(ErrorHandler.handle)
    }

    const getTotalSales = (filters: Filter): void => {
      shipmentsRequest
        .getTotalSales(filters)
        .then((data: Periods) => {
          totalSales.value = data
        })
        .catch(ErrorHandler.handle)
    }

    const getShipmentsCreated = (filters: Filter) => {
      shipmentsRequest
        .getShipmentsCreated(filters)
        .then((data: Periods) => {
          shipmentsCreated.value = data
        })
        .catch(ErrorHandler.handle)
    }

    const getShipmentsSent = (filters: Filter) => {
      shipmentsRequest
        .getShipmentsSent(filters)
        .then((data: Periods) => {
          shipmentsSent.value = data
        })
        .catch(ErrorHandler.handle)
    }

    let deliveryRequest = 0
    const getDeliveryTime = (filters: Filter) => {
      const request = ++deliveryRequest
      deliveryTime.value = []
      Promise.all([
        shipmentsRequest.getDeliveryTime({ ...filters }),
        shipmentsRequest.getUsage({ ...filters }).catch(() => null),
      ])
        .then(([data, counts]) => {
          if (request !== deliveryRequest) return
          deliveryTime.value = counts ? withDeliveryAvailability(data.data, counts.data) : data.data
        })
        .catch(ErrorHandler.handle)
    }

    const getUsage = (filters: Filter): void => {
      shipmentsRequest
        .getUsage(filters)
        .then((data: DataCollection<PeriodsDiff>) => {
          usage.value = data.data
        })
        .catch(ErrorHandler.handle)
    }

    const getAveragePackagePrices = (filters: Filter): void => {
      shipmentsRequest
        .getAveragePackagePrice(filters)
        .then((data: DataCollection<PeriodsDiff>) => {
          averagePrices.value = data.data
        })
        .catch(ErrorHandler.handle)
    }

    const reloadData = (): void => {
      const filters = createFilters()
      getStatsData(filters)
      getCitiesData(filters)
      getTotalSales(filters)
      getShipmentsSent(filters)
      getShipmentsCreated(filters)
      getDeliveryTime({ ...filters, distributionChannel: distributionTypeDeliveryTime.value })
      getUsage({ ...filters, distributionChannel: distributionTypeUsage.value })
      getAveragePackagePrices({ ...filters, serviceLevel: serviceLevel.value })
    }

    onMounted(() => {
      shipmentsRequest
        .getCustomers()
        .then((data: DataCollection<CustomerData>) => {
          customers.value = data.data
          handleCustomerChanged(data.data[0]?.id || '')
        })
        .catch((error) => {
          statsLoading.value = false
          statsError.value = true
          ErrorHandler.handle(error)
        })
    })

    watch([dateRangeValue, customerValue], reloadData)

    return {
      dateRangeValue,
      customerValue,
      stats,
      statsLoading,
      statsError,
      mapMarkers,
      totalSales,
      shipmentsSent,
      shipmentsCreated,
      averagePrices,
      deliveryTime,
      customers,
      filters,
      usage,
      changeDistributionTypeDeliveryTime,
      changeDistributionTypeUsage,
      changeServiceLevel,
      handleDateChanged,
      handleCustomerChanged,
    }
  },
})
</script>
